import React, { useState, useMemo, useCallback, useContext, useEffect, useRef } from 'react';
import { useAuthContext } from '../../../../../common/hooks/use-auth-context.hook';
import { ApiMethod } from '../../../../../common/models/api-types.type';
import { routes } from '../../../../../assets/constants';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, LayoutGrid, PackagePlus, Pencil, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../../../../redux/vendor/ui/uiActions';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

// Layout Components
import { PageContainer } from '../../../../../common/components/PageContainer';
/*
 * Toast comes from its own module rather than the design-system barrel: the
 * barrel pulls in the Lottie overlay, and with it a canvas that jsdom cannot
 * provide, which stops this page's tests from even loading.
 */
import { Toast } from '../../../../../components/design-system/common/Toast';
import { StatusBar } from '../shared/StatusBar';
import { ProductFormLayout } from '../shared/ProductFormLayout';
import { Card } from '../shared/cards/Card';
import { FurnitureDetailsCardV3, type FurniturePlacement } from '../shared/cards/FurnitureDetailsCardV3';
import { GroutCardV3 } from '../shared/cards/GroutCardV3';
import { FinishCardV3 } from '../shared/cards/FinishCardV3';
import { PricePackagingCardV3, type CommerceChange } from '../shared/cards/PricePackagingCardV3';
import { ProductPreviewV3 } from './ProductPreviewV3';
import { GalleryImagesCardV3 } from './GalleryImagesCardV3';
import { FamilySelector } from '../shared/FamilySelector';
import { useBuildFromPhotos } from '../shared/media/build-from-photos.store';
import { BatchProgress } from '../shared/BatchProgress';
import type { BatchStatus } from '../../types/batch.types';

// V3 Hooks and Components
import { useProductFormV3 } from '../../hooks/useProductFormV3';
import { useProductValidationV3 } from '../../hooks/useProductValidationV3';
import { useProductSubmissionV3 } from '../../hooks/useProductSubmissionV3';
import { useVendorFamilies } from '../../../../../common/hooks/use-vendor-families.hook';
import { useTextureApi } from '../../../../../common/hooks/use-texture-api.hook';
import { AxisEditor } from '../axes/AxisEditor';
import { VariantMatrix } from '../variants/VariantMatrix';
import { AttributeFieldRenderer } from '../cards/AttributeFieldRenderer';
import { SPECIAL_ATTRIBUTE_FIELDS } from '../../types';
import type { VariantV3 } from '../../types';
import { getEnabledVariantAxes } from '../../../../../common/models/product-family.model';

// Pattern Requirements Models
import { BricksRequirements } from '../../../seamless-generator/models/BricksRequirements.model';
import { ChevronRequirements } from '../../../seamless-generator/models/ChevronRequirements.model';
import { StackBondRequirements } from '../../../seamless-generator/models/StackBondRequirements.model';
import { HerringboneRequirements } from '../../../seamless-generator/models/HerringboneRequirements.model';
import { HexagonalRequirements } from '../../../seamless-generator/models/HexagonalRequirements.model';
import { PlankRequirements } from '../../../seamless-generator/models/PlankRequirements.model';
import { CabochonRequirements } from '../../../seamless-generator/models/CabochonRequirements.model';
import { cabochonSquareCm, splitCabochonImages } from '../../utils/cabochonImages';
import { OpusRequirements } from '../../../seamless-generator/models/OpusRequirements.model';
import { logger } from '../../../../../utils/logger';

import type { ProductFamily, FamilyAttribute } from '../../../../../common/models/product-family.model';
import { AuthContext } from '../../../../../providers/AuthProvider';
import { config as vendorConfig } from '../../../../../common/hooks/use-settings-api.hook';
import { useProductTypesApi } from '../../../../../common/hooks/use-product-types-api.hook';
import type { ProductType } from '../../../../../common/models/product-type.model';
import { APPLICATION_TYPE } from '../../../my-products/models/product.model';
import { useResolveTranslation } from '../../../../../common/hooks/use-resolve-translation.hook';
import { resolveTranslation } from '../../../../../common/utils/translatable-string';
import { resolveApplicationTypeLabel, dedupeApplicationTypes, isWallOnlyProductType } from '../../../../../common/utils/application-type';

// Setup Dialog
import { CreateProductSetupDialog } from './CreateProductSetupDialog';
import type { SetupResult } from './CreateProductSetupDialog';
import { AiProductSetupFlow } from './AiProductSetupFlow';
import { CreationModeChoice } from './CreationModeChoice';
import type { InitialProductData } from '../../hooks/useProductFormV3';
import { normalizeDisplayUnit, plankCm } from '../../../../../common/utils/units';

// =============================================================================
// Inline SVG Icons for application types
//
// These draw the surface being covered rather than a generic label, so they
// stay hand-drawn; they take their colour from the parent via currentColor.
// =============================================================================

/** The uploader accepts ten; reading more would fetch what cannot be kept. */
const MAX_SEEDED_IMAGES = 10;

/**
 * The file type, from the URL.
 *
 * The bytes come back as an ArrayBuffer with the content type already spent on
 * the wire, and a File still needs a name and a type. The URL is the only
 * thing left that knows, and a shop that serves a .jpg is not lying about it.
 * Anything unrecognisable is treated as a JPEG, which is what a product photo
 * almost always is.
 */
const imageExtensionOf = (url: string): string => {
  const match = /\.(jpe?g|png|webp|avif|gif)(?:[?#]|$)/i.exec(url);
  const ext = match?.[1]?.toLowerCase();
  return !ext ? 'jpg' : ext === 'jpeg' ? 'jpg' : ext;
};

const FloorSvgIcon: React.FC = () => (
  <svg width="16" height="13" viewBox="11 0 42 34" fill="none">
    <path d="M32 4L45 11L45 24L32 31L19 24L19 11L32 4ZM32 4V17" stroke="currentColor" strokeOpacity="0.4" strokeLinejoin="round" strokeWidth="0.8"/>
    <path d="M32 17L45 24L32 31L19 24L32 17Z" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeOpacity="0.6" strokeLinejoin="round" strokeWidth="0.9" strokeDasharray="2 1.5"/>
  </svg>
);

const WallsSvgIcon: React.FC = () => (
  <svg width="16" height="13" viewBox="11 0 42 34" fill="none">
    <path d="M32 31L45 24L45 11L32 4L19 11L19 24L32 31Z" stroke="currentColor" strokeOpacity="0.4" strokeLinejoin="round" strokeWidth="0.8"/>
    <path d="M45 11L32 4V17L45 24L45 11Z" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeOpacity="0.6" strokeLinejoin="round" strokeWidth="0.9" strokeDasharray="2 1.5"/>
    <path d="M32 4L19 11L19 24L32 17V4Z" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeOpacity="0.6" strokeLinejoin="round" strokeWidth="0.9" strokeDasharray="2 1.5"/>
  </svg>
);

const APP_TYPE_SVG: Record<string, React.FC> = {
  [APPLICATION_TYPE.FLOOR]: FloorSvgIcon,
  [APPLICATION_TYPE.WALLS]: WallsSvgIcon,
};

// =============================================================================
// Inline SVG Icons for product types
// =============================================================================

const HardwoodSvgSmall: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M10 2v28" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <path d="M20 2v28" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <path d="M7 8c2-1 4 2 2 5s-3 4-1 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.6"/>
    <path d="M15 6c-1.5 2-2 5 0 8s1 6-1 9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.6"/>
    <path d="M24 5c-1 3 1 5 0 9s1 5 0 8" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.6"/>
  </svg>
);

const TileSvgSmall: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="5" y="5" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6"/>
    <rect x="18" y="5" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6"/>
    <rect x="5" y="18" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6"/>
    <rect x="18" y="18" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6"/>
  </svg>
);

const VinylSvgSmall: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 10h22M5 16h22M5 22h22" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <path d="M16 4v6M12 10v6M20 16v6M14 22v6" stroke="currentColor" strokeWidth="1" strokeOpacity="0.35"/>
  </svg>
);

const LaminateSvgSmall: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 9h22M5 16h22M5 23h22" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <path d="M18 4v5M11 9v7M22 16v7M15 23v5" stroke="currentColor" strokeWidth="1" strokeOpacity="0.4"/>
    <path d="M8 5c1 1 0 3 1 4" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" strokeOpacity="0.35"/>
    <path d="M25 11c-1 1.5 0 3-1 4" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" strokeOpacity="0.35"/>
  </svg>
);

const CarpetingSvgSmall: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 6v20M12 6v20M16 6v20M20 6v20M24 6v20" stroke="currentColor" strokeWidth="0.6" strokeOpacity="0.3"/>
    <path d="M6 8h20M6 12h20M6 16h20M6 20h20M6 24h20" stroke="currentColor" strokeWidth="0.6" strokeOpacity="0.3"/>
    <circle cx="10" cy="10" r="1" fill="currentColor" fillOpacity="0.25"/>
    <circle cx="16" cy="16" r="1" fill="currentColor" fillOpacity="0.25"/>
    <circle cx="22" cy="22" r="1" fill="currentColor" fillOpacity="0.25"/>
    <circle cx="22" cy="10" r="1" fill="currentColor" fillOpacity="0.25"/>
    <circle cx="10" cy="22" r="1" fill="currentColor" fillOpacity="0.25"/>
  </svg>
);

const StonesSvgSmall: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6 13c2-3 6-4 9-2s5 1 8-1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.5"/>
    <path d="M6 21c3-2 5 0 8-1s6-3 9-1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.5"/>
    <path d="M13 5c-1 3-1 6 0 9" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeOpacity="0.4"/>
    <path d="M21 14c1 2 0 5-1 7" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeOpacity="0.4"/>
    <path d="M9 20c0 2 1 4 2 6" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeOpacity="0.4"/>
  </svg>
);

/** Wallpaper — a strip of paper hung from its roll, a motif repeated down it */
const WallpaperSvgSmall: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M10 7h11v19H10z" stroke="currentColor" strokeWidth="1" strokeOpacity="0.6" strokeLinejoin="round"/>
    <path d="M21 7c2 0 3 .9 3 2.2S23 11.5 21 11.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.5"/>
    <path d="M15.5 10l2 2.5-2 2.5-2-2.5zM15.5 18l2 2.5-2 2.5-2-2.5z" stroke="currentColor" strokeWidth="0.8" strokeLinejoin="round" strokeOpacity="0.45"/>
  </svg>
);

const GenericTypeSvgSmall: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
    <rect x="2" y="2" width="28" height="28" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="16" cy="16" r="6" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5"/>
    <circle cx="16" cy="16" r="2" fill="currentColor" fillOpacity="0.3"/>
  </svg>
);

const PRODUCT_TYPE_SVG: Record<string, React.FC> = {
  hardwood: HardwoodSvgSmall,
  tile: TileSvgSmall,
  vinyl: VinylSvgSmall,
  laminate: LaminateSvgSmall,
  carpeting: CarpetingSvgSmall,
  carpet: CarpetingSvgSmall,
  stones: StonesSvgSmall,
  stone: StonesSvgSmall,
  wallpaper: WallpaperSvgSmall,
};

/**
 * A pill the vendor toggles — used for both application types and product
 * types, which differ only in their glyph and label.
 */
const ChoicePill: React.FC<{
  selected: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label: React.ReactNode;
  capitalize?: boolean;
  disabled?: boolean;
}> = ({ selected, onClick, icon, label, capitalize, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'flex items-center gap-2 border px-5 py-2 text-sm transition-colors',
      selected ? 'border-foreground text-foreground' : 'border-input text-muted-foreground hover:border-foreground/40',
      capitalize && 'capitalize',
      disabled && 'cursor-not-allowed opacity-50 hover:border-input',
    )}
  >
    {icon}
    {label}
  </button>
);

/** An uppercase field caption with the required marker the form uses. */
const FieldCaption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="mb-1.5 block text-xs tracking-wider text-muted-foreground uppercase">
    {children} <span className="text-destructive">*</span>
  </span>
);

// =============================================================================
// Types
// =============================================================================

interface EditProductV3PageProps {
  /** Optional vendorId override - if not provided, uses authenticated user's ID */
  vendorId?: string;
}

// =============================================================================
// Module-level store for passing failed variants across create→edit navigation
// =============================================================================

let _pendingFailedVariants: VariantV3[] | null = null;

export function setPendingFailedVariants(v: VariantV3[]) {
  _pendingFailedVariants = v;
}

export function consumePendingFailedVariants(): VariantV3[] | null {
  const result = _pendingFailedVariants;
  _pendingFailedVariants = null;
  return result;
}

// =============================================================================
// Constants
// =============================================================================

const CARD_IDS = {
  BASIC_INFO: 'basic-info',
  ORGANIZATION: 'organization',
  COMMERCE: 'commerce',
  FAMILY_ATTRIBUTES: 'family-attributes',
  AXES: 'axes',
  VARIANTS: 'variants',
  VISUALIZATION: 'visualization',
} as const;

// =============================================================================
// Error page helper — renders a design-system Toast plus a back button
// =============================================================================

const ErrorToastWithBack: React.FC<{ message: string; onBack: () => void; backLabel: string }> = ({ message, onBack, backLabel }) => {
  const [toastVisible, setToastVisible] = useState(true);
  return (
    <div className="p-6">
      <Toast
        message={message}
        type="error"
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
      />
      <Button variant="ghost" onClick={onBack} className="mt-4">
        <ArrowLeft className="rtl:rotate-180" />
        {backLabel}
      </Button>
    </div>
  );
};

// =============================================================================
// Component
// =============================================================================

export const EditProductV3Page: React.FC<EditProductV3PageProps> = ({ vendorId: propVendorId }) => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { rt } = useResolveTranslation();
  const dispatch = useDispatch();
  const isEditMode = !!productId;

  // Get vendorId and vendorName from AuthContext if not provided via props
  const authContext = useContext(AuthContext);
  const vendorId = propVendorId || authContext?.user?._id || '';
  const vendorName = authContext?.user?.vendor?.name || '';
  const unitDisplay = normalizeDisplayUnit(vendorConfig?.unitDisplay);

  // Convert vendor custom fields to FamilyAttribute shape for rendering
  const vendorCustomAttributes: FamilyAttribute[] = useMemo(() => {
    const fields = vendorConfig?.customFields || [];
    return fields.map((cf: any, idx: number) => ({
      fieldName: cf.fieldName,
      fieldLabel: cf.fieldLabel || cf.fieldName,
      fieldType: cf.fieldType || 'text',
      category: 'custom' as any,
      required: cf.required ?? false,
      defaultValue: cf.defaultValue,
      placeholder: cf.placeholder,
      options: cf.options,
      visibleInList: false,
      visibleInFilters: false,
      order: idx,
    }));
  }, [vendorConfig?.customFields]);

  // Family management - always refresh on mount to get latest family config
  const { families, loading: familiesLoading, error: familiesError, getFamilyById, defaultFamily, refresh: refreshFamilies } = useVendorFamilies();
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | undefined>(undefined);

  // Setup dialog state (create mode only)
  const hasScrapedData = !!(location.state as any)?.scrapedProduct;
  const isAiMode = !!(location.state as any)?.aiMode;
  const [creationMode, setCreationMode] = useState<'manual' | 'ai' | null>(
    isEditMode ? 'manual' : isAiMode ? 'ai' : hasScrapedData ? 'manual' : null,
  );
  const [setupComplete, setSetupComplete] = useState<boolean>(isEditMode || hasScrapedData);
  const [pendingSetup, setPendingSetup] = useState<SetupResult | null>(null);
  const [aiInitialData, setAiInitialData] = useState<InitialProductData | undefined>(undefined);

  useEffect(() => {
    refreshFamilies();
  }, []);

  // Determine active family
  const selectedFamily = useMemo((): ProductFamily | null => {
    if (selectedFamilyId) {
      return getFamilyById(selectedFamilyId) || null;
    }
    return defaultFamily || (families.length > 0 ? families[0] : null);
  }, [selectedFamilyId, getFamilyById, defaultFamily, families]);

  // Build initialData from scraped product (passed via navigation state) or AI flow
  const initialData = useMemo(() => {
    if (aiInitialData) return aiInitialData;
    const scraped = (location.state as any)?.scrapedProduct;
    if (!scraped || isEditMode) return undefined;
    return scraped;
  }, [aiInitialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear navigation state after capturing
  useEffect(() => {
    if (initialData || isAiMode) {
      window.history.replaceState({}, document.title);
    }
  }, [initialData, isAiMode]);

  // Form state hook
  const form = useProductFormV3({
    productId,
    family: selectedFamily!,
    vendorId,
    vendorName,
    initialData,
  });

  // In edit mode, sync selectedFamilyId from the loaded product's familyId
  useEffect(() => {
    if (isEditMode && form.product?.familyId && !selectedFamilyId) {
      setSelectedFamilyId(form.product.familyId);
    }
  }, [isEditMode, form.product?.familyId, selectedFamilyId]);

  // Pre-submit snapshot of variants for partial failure recovery
  const preSubmitVariantsRef = useRef<VariantV3[]>([]);

  // Create-to-edit recovery: re-inject failed variants after navigation
  useEffect(() => {
    if (isEditMode && !form.isLoading && form.variants.length > 0) {
      const pending = consumePendingFailedVariants();
      if (pending?.length) {
        form.appendVariants(pending);
      }
    }
  }, [isEditMode, form.isLoading, form.variants.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get attributes to display in the dynamic attributes card
  // Filter out fields that have dedicated UI (not stored in attributes object)
  const dynamicAttributes = useMemo(() => {
    const allAttributes = selectedFamily?.attributes || [];
    return allAttributes.filter(attr => !SPECIAL_ATTRIBUTE_FIELDS.includes(attr.fieldName as any));
  }, [selectedFamily?.attributes]);

  // State for material images (lifted from VariantsCardContent for submission)
  const [uploadedPlankImages, setUploadedPlankImages] = useState<{ [plankGroupId: string]: File[] }>({});

  /**
   * The photos the reader found, fetched and put in the uploader.
   *
   * The scrape already returns them and the result screen already shows them,
   * so the vendor arrived at an empty "Upload plank/tile images" box directly
   * beneath a preview of the very images that belonged in it — the one manual
   * step the feature exists to remove.
   *
   * Through the API rather than straight from the browser: the images live on
   * the vendor's own shop, which sends no CORS headers to our origin, so a
   * fetch from here is blocked before it starts. The server read the page from
   * that host already.
   *
   * Seeded, not forced. They land in the uploader as ordinary files the vendor
   * can remove or add to, because a reader that picked a lifestyle shot must be
   * correctable without starting again.
   */
  const { sendAuthGuardedRequest } = useAuthContext();
  const seededImagesFor = useRef<string | null>(null);
  useEffect(() => {
    const urls = initialData?.imageUrls;
    const first = form.variants[0];
    // The family decides where the photos go: wait for it.
    if (!urls?.length || !first || isEditMode || !selectedFamily) return;

    const plankGroupId = first.visualization?.surface?.plankGroupId || first.uuid;
    if (seededImagesFor.current === plankGroupId) return;
    if (uploadedPlankImages[plankGroupId]?.length) return;
    seededImagesFor.current = plankGroupId;

    // A surface's page photos are rarely tiles as they are (a sheet, a room, a
    // tile on a table): they go to Build from photos first, which cuts the
    // tiles out of them. Its dialog opens on the first variant with the photos
    // ticked; the vendor starts it, since it spends credits. The API reads them
    // from the shop, so nothing is downloaded here.
    const vis = selectedFamily?.visualizationConfig;
    if (vis?.requiresSeamlessTexture && !vis?.requires3DModel) {
      useBuildFromPhotos.getState().offer(first.uuid, urls);
      return;
    }

    let cancelled = false;
    void (async () => {
      const files: File[] = [];
      // The uploader's own ceiling. Reading more would spend time on images
      // that cannot be kept.
      for (const url of urls.slice(0, MAX_SEEDED_IMAGES)) {
        try {
          const bytes = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.products.scrapeImage,
            { url },
            { responseType: 'arraybuffer' } as RequestInit,
          ) as ArrayBuffer;
          if (!bytes?.byteLength) continue;

          const ext = imageExtensionOf(url);
          files.push(new File(
            [bytes],
            `scraped_${files.length + 1}.${ext}`,
            { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` },
          ));
        } catch {
          // One unreadable image is not a failed import — a shop that blocks
          // hotlinking on some assets should still hand over the rest.
        }
      }
      if (!cancelled && files.length) {
        setUploadedPlankImages((prev) => ({ ...prev, [plankGroupId]: files }));
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, form.variants.length, isEditMode, selectedFamily]);

  // Product type patterns — updated when a product type is selected
  const [productTypePatterns, setProductTypePatterns] = useState<string[]>([]);

  // Product types for inline selectors (moved from OrganizationCard)
  const [allProductTypes, setAllProductTypes] = useState<ProductType[]>([]);
  const productTypesApi = useProductTypesApi();

  // Fetch product types from API
  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const types = await productTypesApi.findAllProductTypes({ activeOnly: true });
        setAllProductTypes(types);
      } catch (error) {
        console.error('Error fetching product types:', error);
      }
    };
    fetchProductTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter product types based on selected application types
  const availableProductTypes = useMemo(() => {
    const appTypes = (form.product?.organization?.applicationTypes as APPLICATION_TYPE[]) || [];
    return allProductTypes.filter(pt =>
      pt.applicationTypes.some(appType => appTypes.includes(appType as APPLICATION_TYPE))
    );
  }, [form.product?.organization?.applicationTypes, allProductTypes]);

  // Emit product type patterns when product type or available types change
  useEffect(() => {
    const currentType = form.product?.organization?.productType;
    if (!currentType) return;
    const selectedType = allProductTypes.find(
      pt => resolveTranslation(pt.name, 'en').toLowerCase() === currentType.toLowerCase()
        || pt.slug?.toLowerCase() === currentType.toLowerCase()
    );
    if (selectedType) {
      setProductTypePatterns(selectedType.patterns.map(p => p.name));
      // If matched by slug, normalize to English name for consistent storage
      const englishName = resolveTranslation(selectedType.name, 'en');
      if (currentType.toLowerCase() !== englishName.toLowerCase()) {
        form.setField('organization.productType', englishName);
      }
    }
  }, [form.product?.organization?.productType, allProductTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validation hook
  const validation = useProductValidationV3({
    product: form.product,
    family: selectedFamily!,
    variants: form.variants,
    uploadedPlankImages,
    realTimeValidation: true,
  });

  // Submission hook
  const submission = useProductSubmissionV3({
    product: form.product,
    variants: form.variants,
    family: selectedFamily!,
    uploadedPlankImages,
    originalVariantUuids: form.originalVariantUuids,
    onSuccess: (result) => {
      // Extract failed variants from pre-submit snapshot
      const failedUuids = new Set(
        (result.jobs || [])
          .filter(j => j.status === 'failed')
          .map(j => j.uuid)
          .filter(Boolean) as string[]
      );
      const failedVariants = failedUuids.size > 0
        ? preSubmitVariantsRef.current
            .filter(v => failedUuids.has(v.uuid))
            .map(v => {
              const job = result.jobs?.find(j => j.uuid === v.uuid);
              return {
                ...v,
                _isNew: true,
                _error: {
                  message: job?.error || 'Submission failed',
                  details: job?.errorDetails,
                },
              };
            })
        : [];

      if (!isEditMode && result.jobs?.length) {
        // Create mode: navigate to the edit URL using the backend product ID
        const defaultJob = result.jobs.find(j => j.status === 'completed' && j.resultProduct?.id);
        if (defaultJob?.resultProduct?.id) {
          if (failedVariants.length > 0) {
            setPendingFailedVariants(failedVariants);
          }
          navigate(`/products/${defaultJob.resultProduct.id}/edit`, { replace: true });
        }
      } else {
        // Edit mode: check if the current URL variant was deleted
        const deletedUuids = new Set(
          (result.jobs || [])
            .filter(j => j.operation === 'delete')
            .map(j => j.uuid)
            .filter(Boolean) as string[]
        );

        if (productId && deletedUuids.has(productId)) {
          // URL variant was deleted — find the new default to navigate to
          const newDefault = preSubmitVariantsRef.current.find(
            v => v.isDefault && v.isSelected && !deletedUuids.has(v.uuid)
          );
          const targetUuid = newDefault?.uuid
            || (result.jobs || []).find(
                j => j.status === 'completed' && j.operation !== 'delete' && j.uuid
              )?.uuid;

          if (targetUuid) {
            if (failedVariants.length > 0) {
              setPendingFailedVariants(failedVariants);
            }
            navigate(`/products/${targetUuid}/edit`, { replace: true });
            return;
          }
        }

        // Normal path: stay on page, silently reload form data with failed variants
        form.reload(true, failedVariants.length > 0 ? failedVariants : undefined);
      }
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save product.',
        severity: 'error',
      });
    },
  });

  // Derive isSubmitting from submission progress
  const isSubmitting = submission.progress.status === 'validating' || submission.progress.status === 'submitting';
  // Track which action (save vs publish) triggered the current submission so
  // the StatusBar can show the spinner only on the clicked button. Reset to
  // null whenever submission isn't in-flight.
  const [submittingAction, setSubmittingAction] = useState<'save' | 'publish' | null>(null);
  useEffect(() => {
    if (!isSubmitting) setSubmittingAction(null);
  }, [isSubmitting]);
  const isSubmissionComplete = submission.progress.status === 'success' || submission.progress.status === 'error';

  // Map V3 submission progress to V2 BatchStatus for BatchProgress component
  const batchStatus: BatchStatus | null = useMemo(() => {
    const p = submission.progress;
    if (p.status === 'idle') return null;

    return {
      batchId: p.batchId || '',
      total: p.total,
      pending: Math.max(0, p.total - p.processed - (p.status === 'submitting' ? 1 : 0)),
      processing: p.status === 'submitting' && p.processed < p.total ? 1 : 0,
      completed: p.succeeded,
      failed: p.failed,
      percentage: p.total > 0 ? Math.round((p.processed / p.total) * 100) : 0,
      jobs: (p.jobs || []).map(job => ({
        jobId: job.jobId,
        status: job.status as any,
        operation: job.operation as any,
        productData: job.productData as any,
        resultProduct: job.resultProduct as any,
        error: job.error,
        errorDetails: job.errorDetails as any,
      })),
      errors: [],
    };
  }, [submission.progress]);

  // UI state
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [organizationExpanded, setOrganizationExpanded] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // The notice used to be a MUI Snackbar, which hid itself; keep that.
  useEffect(() => {
    if (!snackbar.open) return;
    const timer = window.setTimeout(() => setSnackbar((sb) => ({ ...sb, open: false })), 6000);
    return () => window.clearTimeout(timer);
  }, [snackbar.open, snackbar.message]);

  // Only show validation errors after user clicks Save
  const shouldShowErrors = hasAttemptedSave;

  // Get validation errors for cards
  const getCardErrors = useCallback(
    (cardId: string): string[] => {
      if (!shouldShowErrors) return [];
      return validation.result.errorsByCard[cardId] || [];
    },
    [validation.result, shouldShowErrors]
  );

  // Handle family selection
  const handleFamilySelect = useCallback(
    (familyId: string) => {
      if (!isEditMode) {
        setSelectedFamilyId(familyId);
      }
    },
    [isEditMode]
  );

  // Handle application type toggle (inline in Product Family card)
  const handleToggleApplicationType = useCallback((appType: APPLICATION_TYPE) => {
    const currentTypes = (form.product?.organization?.applicationTypes as APPLICATION_TYPE[]) || [];
    // A wallpaper stays on the walls: the floor cannot be added, nor the walls taken away.
    if (isWallOnlyProductType(form.product?.organization?.productType)) return;
    let newTypes: APPLICATION_TYPE[];
    if (currentTypes.includes(appType)) {
      newTypes = currentTypes.filter(t => t !== appType);
    } else {
      newTypes = [...currentTypes, appType];
    }
    form.setField('organization.applicationTypes', newTypes);
    // Only clear product type if it's no longer compatible with the new application types
    const currentProductType = form.product?.organization?.productType;
    if (currentProductType) {
      const ptDef = allProductTypes.find(
        pt => resolveTranslation(pt.name, 'en').toLowerCase() === currentProductType.toLowerCase()
          || pt.slug?.toLowerCase() === currentProductType.toLowerCase()
      );
      const stillCompatible = ptDef?.applicationTypes.some(
        at => newTypes.includes(at as APPLICATION_TYPE)
      );
      if (!stillCompatible) {
        form.setField('organization.productType', '');
      }
    }
  }, [form, allProductTypes]);

  // Handle product type selection (inline in Product Family card)
  const handleTypeChange = useCallback((newType: string) => {
    form.setField('organization.productType', newType);
    // A wallpaper goes on walls only (ARDE-296).
    if (isWallOnlyProductType(newType)) {
      form.setField('organization.applicationTypes', [APPLICATION_TYPE.WALLS]);
    }
    // Immediately emit the selected type's patterns
    const selectedType = allProductTypes.find(
      pt => resolveTranslation(pt.name, 'en').toLowerCase() === newType.toLowerCase()
        || pt.slug?.toLowerCase() === newType.toLowerCase()
    );
    if (selectedType) {
      setProductTypePatterns(selectedType.patterns.map(p => p.name));
    }
  }, [form, allProductTypes]);

  // Handle setup dialog completion
  const handleSetupComplete = useCallback((result: SetupResult) => {
    setSelectedFamilyId(result.familyId);
    setPendingSetup(result);
    setSetupComplete(true);
  }, []);

  // Handle AI setup flow completion
  const handleAiComplete = useCallback((setup: SetupResult, scrapedData: InitialProductData) => {
    setSelectedFamilyId(setup.familyId);
    setPendingSetup(setup);
    setAiInitialData(scrapedData);
    setSetupComplete(true);
  }, []);

  // Apply pending setup once the family has fully loaded
  useEffect(() => {
    if (pendingSetup && selectedFamily?._id === pendingSetup.familyId) {
      if (isWallOnlyProductType(pendingSetup.productType)) {
        // A wallpaper goes on walls only, whatever the setup or the AI read said.
        form.setField('organization.applicationTypes', [APPLICATION_TYPE.WALLS]);
      } else if (pendingSetup.applicationTypes) {
        form.setField('organization.applicationTypes', pendingSetup.applicationTypes);
      }
      if (pendingSetup.productType) {
        form.setField('organization.productType', pendingSetup.productType);
      }
      setPendingSetup(null);
    }
  }, [pendingSetup, selectedFamily]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle save
  //
  // For drafts (products created by an API integration sync), Save persists
  // partial progress without leaving draft state — the vendor should be able
  // to come back later and keep editing. Validation is advisory here: errors
  // still surface inline via `hasAttemptedSave` + the StatusBar tooltip, but
  // they don't block the save.
  //
  // For regular (non-draft) products, Save keeps requiring a valid form so
  // published catalog entries don't regress into a broken state.
  const handleSave = useCallback(async () => {
    setHasAttemptedSave(true);
    const validationResult = validation.validate();
    const isDraftSave = !!form.product?.isDraft;

    if (!validationResult.isValid && !isDraftSave) {
      setSnackbar({
        open: true,
        message: t('PAGES.NEW_PRODUCT.V3.FIX_ERRORS_BEFORE_SAVING', { count: validationResult.errorCount }),
        severity: 'error',
      });
      return;
    }

    // Snapshot variants before submission for partial failure recovery
    preSubmitVariantsRef.current = form.variants.map(v => ({ ...v }));

    // Submit via useProductSubmissionV3 hook
    // The hook handles success/error callbacks configured above
    setSubmittingAction('save');
    await submission.submit();
  }, [validation, submission, form.variants, form.product?.isDraft]);

  // Handle publish draft — same pipeline as save, but flips `isDraft: false`
  // + `isVisible: true` on every variant so the product leaves draft state
  // and becomes visible in the catalog and visualizer.
  const handlePublishDraft = useCallback(async () => {
    setHasAttemptedSave(true);
    const validationResult = validation.validate();

    if (!validationResult.isValid) {
      setSnackbar({
        open: true,
        message: t('PAGES.NEW_PRODUCT.V3.FIX_ERRORS_BEFORE_SAVING', { count: validationResult.errorCount }),
        severity: 'error',
      });
      return;
    }

    preSubmitVariantsRef.current = form.variants.map(v => ({ ...v }));
    setSubmittingAction('publish');
    await submission.submit({ publishDraft: true });
  }, [validation, submission, form.variants]);

  // Handle discard/reset
  const handleDiscard = useCallback(() => {
    form.reset();
    validation.clearErrors();
    submission.reset();
    setHasAttemptedSave(false);
  }, [form, validation, submission]);

  // Sync loading state with the global overlay
  // Only treat form as loading when we actually have a family (avoids deadlock when families fail/are empty)
  const isPageLoading = familiesLoading || (!!selectedFamily && form.isLoading);
  useEffect(() => {
    dispatch(setLoading(isPageLoading, isPageLoading
      ? (form.isLoading ? 'Loading product...' : 'Loading families...')
      : 'Loading...'
    ));
  }, [isPageLoading, form.isLoading, dispatch]);

  // While loading, render nothing — the global overlay handles the UX
  if (isPageLoading) {
    return null;
  }

  // Families / form error state — show a top-down toast instead of an inline banner
  const pageLevelError = familiesError || form.error;
  if (pageLevelError) {
    return (
      <PageContainer
        breadcrumbs={[
          { label: t('SIDEBAR.PRODUCTS.TITLE') },
          {
            label: t('SIDEBAR.PRODUCTS.CATALOG'),
            icon: <LayoutGrid className="size-5" />,
            href: '/products/catalog',
          },
          {
            label: isEditMode ? t('PAGES.NEW_PRODUCT.V3.EDIT_PRODUCT') : t('SIDEBAR.PRODUCTS.NEW_PRODUCT'),
          },
        ]}
      >
        <ErrorToastWithBack
          message={pageLevelError}
          onBack={() => navigate('/products/catalog')}
          backLabel={t('PAGES.NEW_PRODUCT.V3.BACK_TO_PRODUCTS')}
        />
      </PageContainer>
    );
  }

  // No family available
  if (!selectedFamily) {
    return (
      <PageContainer
        breadcrumbs={[
          { label: t('SIDEBAR.PRODUCTS.TITLE') },
          {
            label: t('SIDEBAR.PRODUCTS.CATALOG'),
            icon: <LayoutGrid className="size-5" />,
            href: '/products/catalog',
          },
          {
            label: isEditMode ? t('PAGES.NEW_PRODUCT.V3.EDIT_PRODUCT') : t('SIDEBAR.PRODUCTS.NEW_PRODUCT'),
          },
        ]}
      >
        <div className="p-6">
          <Alert>
            <AlertDescription className="text-warning">
              {t('PAGES.NEW_PRODUCT.V3.NO_FAMILY_AVAILABLE')}
            </AlertDescription>
          </Alert>
        </div>
      </PageContainer>
    );
  }

  // No product (shouldn't happen but safety check)
  if (!form.product) {
    return null;
  }

  // Derived values
  const enabledAxes = getEnabledVariantAxes(selectedFamily);
  const visualizationType = selectedFamily.visualizationConfig?.requires3DModel
    ? 'model3d'
    : selectedFamily.visualizationConfig?.requiresSeamlessTexture
      ? 'surface'
      : 'image_only';

  // Furniture (3D-model families): placement is stored product-level on the form
  // (works in add mode before any variants exist) and injected into each variant
  // at submission (see variantToProductPayload).
  const isFurnitureFamily = visualizationType === 'model3d';
  const currentPlacement = (
    form.product?.visualization?.model3d?.arPlacement
    ?? (form.variants.find((v) => v.isDefault) || form.variants[0])?.visualization?.model3d?.arPlacement
  ) as FurniturePlacement | undefined;
  const handlePlacementChange = (placement: FurniturePlacement) => {
    form.setField('visualization.model3d.arPlacement', placement);
  };

  // Surface families: whether shoppers may change the grout in the visualizer
  // (grout v2). Product-level like the placement, stamped onto every variant.
  const isSurfaceFamily = visualizationType === 'surface';

  /** Price & packaging card: new commerce per variant, and on the product so later variants inherit it. */
  const handleCommerceChanges = (changes: CommerceChange[], productCommerce?: VariantV3['commerce']) => {
    changes.forEach(({ uuid, commerce }) => form.setVariantField(uuid, 'commerce', commerce));
    if (productCommerce) form.setField('commerce', productCommerce);
  };
  const groutEditable = !!(
    form.product?.visualization?.surface?.groutEditable
    ?? (form.variants.find((v) => v.isDefault) || form.variants[0])?.visualization?.surface?.groutEditable
  );
  // How it reflects light: product-level like the grout switch. Only for vendors
  // Ardealy has switched finishes on for; elsewhere it would change nothing.
  const showFinish = isSurfaceFamily && vendorConfig?.finishesEnabled === true;
  const finish = form.product?.visualization?.surface?.finish
    ?? (form.variants.find((v) => v.isDefault) || form.variants[0])?.visualization?.surface?.finish;

  return (
    <PageContainer
      breadcrumbs={[
        { label: t('SIDEBAR.PRODUCTS.TITLE') },
        {
          label: isEditMode ? t('PAGES.NEW_PRODUCT.V3.EDIT_PRODUCT') : t('SIDEBAR.PRODUCTS.NEW_PRODUCT'),
          icon: <PackagePlus className="size-5" />,
        },
      ]}
    >
      {/* Creation mode choice (create mode only, before any setup) */}
      {!isEditMode && !creationMode && !setupComplete && (
        <CreationModeChoice
          onSelectManual={() => setCreationMode('manual')}
          onSelectAi={() => setCreationMode('ai')}
          onSelectImport={() => navigate('/products/import')}
          onCancel={() => navigate('/products/catalog')}
        />
      )}

      {/* Product Setup Flow (manual mode) */}
      {!isEditMode && creationMode === 'manual' && !setupComplete && !familiesLoading && families.length > 0 && (
        <CreateProductSetupDialog
          open={true}
          families={families}
          familiesLoading={familiesLoading}
          onComplete={handleSetupComplete}
          onCancel={() => setCreationMode(null)}
        />
      )}

      {/* AI Product Setup Flow */}
      {!isEditMode && creationMode === 'ai' && !setupComplete && families.length > 0 && (
        <AiProductSetupFlow
          families={families}
          familiesLoading={familiesLoading}
          onComplete={handleAiComplete}
          onCancel={() => setCreationMode(null)}
        />
      )}

      {/* Editor content — shown after setup is complete (or always in edit mode) */}
      {(isEditMode || setupComplete) && (
        <>
      {/* Status Bar - Shopify style */}
      <StatusBar
        title={form.product.name || t('PAGES.NEW_PRODUCT.V3.STATUS_BAR.NEW_PRODUCT')}
        hasChanges={form.isDirty}
        isValid={validation.result.isValid}
        validationErrors={validation.result.messages.map(m => m.message)}
        isSubmitting={isSubmitting}
        submittingAction={submittingAction}
        onSave={handleSave}
        onDiscard={handleDiscard}
        productSku={form.product.sku}
        isDraft={!!form.product.isDraft}
        onPublishDraft={handlePublishDraft}
      />

      {/* Hero Title Area — Product name as page headline, brand as subtitle */}
      <div className="mb-2">
        <input
          value={form.product.name || ''}
          onChange={(e) => form.setField('name', e.target.value)}
          placeholder={t('PAGES.NEW_PRODUCT.V3.STATUS_BAR.NEW_PRODUCT')}
          aria-invalid={
            (shouldShowErrors && (validation.getFieldErrors('name') || []).length > 0) || undefined
          }
          className="w-full border-0 bg-transparent p-0 font-heading text-[28px] leading-tight font-medium tracking-tight text-foreground outline-none placeholder:text-muted-foreground aria-invalid:text-destructive"
        />
        <input
          value={form.product.brand || ''}
          onChange={(e) => form.setField('brand', e.target.value)}
          placeholder={t('PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.BRAND') || 'Brand'}
          aria-invalid={
            (shouldShowErrors && (validation.getFieldErrors('brand') || []).length > 0) || undefined
          }
          className="w-full border-0 bg-transparent p-0 text-sm text-muted-foreground outline-none placeholder:text-muted-foreground/60 aria-invalid:text-destructive"
        />
      </div>

      {/* Organization Context Bar — inline pills showing family/type/application */}
      {setupComplete && (families.length > 1 || selectedFamily?.visualizationConfig?.showApplicationTypes || selectedFamily?.visualizationConfig?.showProductType) && (
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="organization-summary">
          <Badge
            variant="outline"
            asChild
          >
            <button type="button" onClick={() => setOrganizationExpanded(!organizationExpanded)}>
              {rt(selectedFamily.name)}
            </button>
          </Badge>

          {selectedFamily?.visualizationConfig?.showApplicationTypes && (() => {
            const appTypes = dedupeApplicationTypes((form.product.organization?.applicationTypes as APPLICATION_TYPE[]) || []) as APPLICATION_TYPE[];
            return appTypes.map((appType) => {
              return (
                <Badge key={appType} variant="outline" className="font-normal">
                  {resolveApplicationTypeLabel(t, appType)}
                </Badge>
              );
            });
          })()}

          {selectedFamily?.visualizationConfig?.showProductType && form.product.organization?.productType && (() => {
            const ptStoredName = form.product.organization.productType;
            const ptDef = allProductTypes.find(pt => resolveTranslation(pt.name, 'en').toLowerCase() === ptStoredName.toLowerCase());
            const ptDisplayName = ptDef ? rt(ptDef.name) : ptStoredName;
            return (
              <Badge variant="outline" className="font-normal capitalize">
                {ptDisplayName}
              </Badge>
            );
          })()}

          {/* Edit organization button */}
          <button
            type="button"
            onClick={() => setOrganizationExpanded(!organizationExpanded)}
            data-testid="organization-edit-button"
            className="inline-flex h-5 items-center gap-1 border border-dashed border-input px-2 text-xs font-medium text-muted-foreground transition-colors hover:border-muted-foreground hover:bg-muted [&_svg]:size-3.5"
          >
            {organizationExpanded ? <Check /> : <Pencil />}
            {organizationExpanded ? t('COMMON.DONE') : t('COMMON.EDIT')}
          </button>
        </div>
      )}

      {/* Organization Expanded Selectors — inline below pill bar */}
      {organizationExpanded && (
        <div className="mb-6 flex flex-col gap-5">
          {families.length > 1 && (
            <div>
              <FieldCaption>{t('PAGES.NEW_PRODUCT.V3.PRODUCT_FAMILY')}</FieldCaption>
              <FamilySelector families={families} selectedFamilyId={selectedFamily._id} onFamilySelect={handleFamilySelect} loading={familiesLoading} disabled={isEditMode} />
            </div>
          )}
          {selectedFamily?.visualizationConfig?.showApplicationTypes && (
            <div>
              <FieldCaption>{t('PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.APPLICATION')}</FieldCaption>
              <div className="flex flex-wrap gap-2">
                {[APPLICATION_TYPE.FLOOR, APPLICATION_TYPE.WALLS].map((appType) => {
                  const appTypes = (form.product.organization?.applicationTypes as APPLICATION_TYPE[]) || [];
                  const isSelected = appTypes.includes(appType);
                  const AppIcon = APP_TYPE_SVG[appType];
                  return (
                    <ChoicePill
                      key={appType}
                      selected={isSelected}
                      onClick={() => handleToggleApplicationType(appType)}
                      icon={AppIcon ? <AppIcon /> : undefined}
                      label={t(`PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.${appType.toUpperCase()}`)}
                      disabled={isWallOnlyProductType(form.product.organization?.productType)}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {selectedFamily?.visualizationConfig?.showProductType && (
            <div>
              <FieldCaption>{t('PAGES.NEW_PRODUCT.VARIANTS.TYPE')}</FieldCaption>
              {availableProductTypes.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {availableProductTypes.map((productType) => {
                    const ptEnglishName = resolveTranslation(productType.name, 'en');
                    const ptDisplayName = rt(productType.name);
                    const isSelected = (form.product.organization?.productType || '').toLowerCase() === ptEnglishName.toLowerCase();
                    const PtIcon = PRODUCT_TYPE_SVG[productType.slug?.toLowerCase()] || GenericTypeSvgSmall;
                    return (
                      <ChoicePill
                        key={productType.slug}
                        selected={isSelected}
                        onClick={() => handleTypeChange(ptEnglishName)}
                        icon={<PtIcon />}
                        label={ptDisplayName}
                        capitalize
                      />
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">{t('PAGES.NEW_PRODUCT.V2.SELECT_APPLICATION_FIRST')}</p>
              )}
              {isWallOnlyProductType(form.product.organization?.productType) && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="wallpaper-hint">
                  {t('PAGES.NEW_PRODUCT.V3.WALLPAPER_HINT', 'Wallpaper goes on walls only. Enter the size as the roll width × the pattern repeat (not the roll length), and upload one image of a single repeat.')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Layout - Two Column (8/4 grid) */}
      <ProductFormLayout
        sidebar={
          <>
            <ProductPreviewV3
              product={form.product}
              variants={form.variants}
              variantAxes={selectedFamily?.variantAxes || []}
            />
            <GalleryImagesCardV3
              variant={
                form.variants.find((v) => v.isDefault) ||
                form.variants.find((v) => v.isSelected) ||
                form.variants[0] ||
                null
              }
              onChange={(patch) => {
                const target =
                  form.variants.find((v) => v.isDefault) ||
                  form.variants.find((v) => v.isSelected) ||
                  form.variants[0];
                if (target) form.patchVariant(target.uuid, patch);
              }}
            />
          </>
        }
      >
        {/* Details Card — description, collections, family attributes merged */}
        <Card
          title={t('PAGES.NEW_PRODUCT.V2.TITLE_DESCRIPTION_CARD')}
          collapsible
          defaultExpanded
          errors={shouldShowErrors ? [...getCardErrors(CARD_IDS.BASIC_INFO), ...getCardErrors(CARD_IDS.FAMILY_ATTRIBUTES)] : []}
        >
          <BasicInfoCardContent
            description={form.product.description || ''}
            collections={(form.product.attributes?.collections as string[]) || []}
            onChange={(field, value) => form.setField(field, value)}
            onCollectionsChange={(collections) => form.setField('attributes.collections', collections)}
            familyAttributes={selectedFamily?.attributes}
            errors={{
              description: shouldShowErrors ? validation.getFieldErrors('description') : [],
            }}
          />

          {/* Family-specific attributes inline */}
          {dynamicAttributes.length > 0 && (
            <div className="mt-5 border-t pt-5">
              <FamilyAttributesContent
                attributes={dynamicAttributes}
                values={form.product.attributes || {}}
                onAttributeChange={(key, value) => form.setField(`attributes.${key}`, value)}
              />
            </div>
          )}

          {/* Vendor custom fields */}
          {vendorCustomAttributes.length > 0 && (
            <div className="mt-5 border-t pt-5">
              <p className="mb-3 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                {t('PAGES.NEW_PRODUCT.V3.CUSTOM_FIELDS', 'Custom fields')}
              </p>
              <FamilyAttributesContent
                attributes={vendorCustomAttributes}
                values={form.product.attributes || {}}
                onAttributeChange={(key, value) => form.setField(`attributes.${key}`, value)}
              />
            </div>
          )}
        </Card>

        {/* Furniture details — category + floor/wall placement (3D-model families only) */}
        {isFurnitureFamily && (
          <FurnitureDetailsCardV3
            category={form.product.category}
            onCategoryChange={(slug) => form.setField('category', slug)}
            placement={currentPlacement}
            onPlacementChange={handlePlacementChange}
          />
        )}

        {/* Grout — may shoppers change it in the visualizer (surface families only) */}
        {isSurfaceFamily && (
          <GroutCardV3
            checked={groutEditable}
            onCheckedChange={(checked) => form.setField('visualization.surface.groutEditable', checked)}
          />
        )}

        {/* Finish — how it reflects light, so shoppers may turn reflections on (surface families only) */}
        {showFinish && (
          <FinishCardV3
            value={finish}
            onChange={(next) => form.setField('visualization.surface.finish', next)}
          />
        )}

        {/* Price & packaging — what each variant sells for, its box, its samples (every family) */}
        {form.product && (
          <PricePackagingCardV3
            product={form.product}
            variants={form.variants}
            isSurface={isSurfaceFamily}
            onApply={handleCommerceChanges}
          />
        )}

        {/* Axes Card — define your options (colors, sizes, patterns) */}
        {enabledAxes.length > 0 && form.product && (
          <Card
            title={t('PAGES.NEW_PRODUCT.V3.AXES.TITLE')}
            errors={shouldShowErrors ? getCardErrors(CARD_IDS.AXES) : []}
          >
            <div className="flex flex-col gap-5">
              {enabledAxes.map((axis: any) => {
                const axisConfig = axis.name === 'pattern' && productTypePatterns && productTypePatterns.length > 0
                  ? { ...axis, predefinedValues: productTypePatterns }
                  : axis;
                return (
                  <AxisEditor
                    key={axis.name}
                    config={axisConfig}
                    values={form.getAllAxisValues()[axis.name] || []}
                    onChange={(values) => form.setAxisValues(axis.name, values)}
                    variants={form.variants}
                    onDeleteVariants={(uuids) => uuids.forEach((uuid) => form.deleteVariant(uuid))}
                    onAssignAxisValueToVariants={form.assignAxisValueToVariants}
                    unitDisplay={unitDisplay}
                  />
                );
              })}
            </div>
          </Card>
        )}

        {/* Variants Card — the generated variant matrix */}
        {enabledAxes.length > 0 && form.product && form.variants.length > 0 && (
          <Card
            title={t('PAGES.NEW_PRODUCT.V2.VARIANTS_CARD')}
            collapsible
            defaultExpanded
            action={
              <Badge variant="outline" className="text-muted-foreground">
                {form.variants.length} variant{form.variants.length !== 1 ? 's' : ''}
              </Badge>
            }
            errors={shouldShowErrors ? getCardErrors(CARD_IDS.VARIANTS) : []}
          >
            <VariantsCardContent
              family={selectedFamily}
              axisValues={form.getAllAxisValues()}
              onAxisValuesChange={form.setAxisValues}
              onRegenerateVariants={form.regenerateVariants}
              variants={form.variants}
              variantGroups={form.variantGroups}
              enabledAxes={enabledAxes}
              visualizationType={visualizationType}
              productTypePatterns={productTypePatterns}
              onVariantChange={form.setVariantField}
              onDeleteVariant={form.deleteVariant}
              onSetDefault={form.setDefaultVariant}
              onSelectVariant={form.selectVariant}
              onSelectAll={form.selectAllVariants}
              onVisualizationChange={form.setVisualizationField}
              onReorderVariants={form.reorderVariants}
              onAssignAxisValueToVariants={form.assignAxisValueToVariants}
              unitDisplay={unitDisplay}
              uploadedPlankImages={uploadedPlankImages}
              onUploadedPlankImagesChange={setUploadedPlankImages}
              setPendingFile={submission.setPendingFile}
              getPendingFile={submission.getPendingFile}
            />
          </Card>
        )}
      </ProductFormLayout>

      {/* Notification for save / validation outcomes */}
      {snackbar.open && (
        <div className="fixed bottom-[calc(1.5rem+var(--support-fab-clearance,0px))] end-6 z-50 max-w-sm">
          <Alert variant={snackbar.severity === 'error' ? 'destructive' : 'default'} className="shadow-lg">
            <AlertDescription
              className={cn(snackbar.severity === 'error' ? 'text-destructive' : 'text-success')}
            >
              {snackbar.message}
            </AlertDescription>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={t('COMMON.CLOSE', 'Close')}
              onClick={() => setSnackbar((sb) => ({ ...sb, open: false }))}
              className="absolute end-1 top-1"
            >
              <X />
            </Button>
          </Alert>
        </div>
      )}

      {/* Batch Progress overlay (fixed bottom-right) */}
      {batchStatus && (
        <BatchProgress
          batchStatus={batchStatus}
          isComplete={isSubmissionComplete}
          onBackToCatalog={() => navigate('/products/catalog')}
          onClose={() => {
            submission.reset();
          }}
        />
      )}
        </>
      )}

    </PageContainer>
  );
};

// =============================================================================
// Helper Components - Inline content without wrapper
// =============================================================================

interface BasicInfoCardContentProps {
  description: string;
  collections?: string[];
  onChange: (field: 'description', value: string) => void;
  onCollectionsChange?: (collections: string[]) => void;
  errors?: {
    description?: string[];
  };
  /** Family attribute definitions — used to resolve translatable labels/placeholders */
  familyAttributes?: FamilyAttribute[];
}

const BasicInfoCardContent: React.FC<BasicInfoCardContentProps> = ({
  description,
  collections = [],
  onChange,
  onCollectionsChange,
  errors = {},
  familyAttributes = [],
}) => {
  const { t } = useTranslation();
  const { rt } = useResolveTranslation();
  const [newCollection, setNewCollection] = useState('');

  // Look up translatable labels/placeholders from family attribute definitions
  const getAttr = (fieldName: string) => familyAttributes.find(a => a.fieldName === fieldName);
  const descAttr = getAttr('description');
  const collectionsAttr = getAttr('collections');

  const getFieldError = (field: keyof typeof errors): string | undefined => {
    const fieldErrors = errors[field];
    return fieldErrors && fieldErrors.length > 0 ? fieldErrors[0] : undefined;
  };

  const handleAddCollection = () => {
    if (newCollection.trim() && !collections.includes(newCollection.trim()) && onCollectionsChange) {
      onCollectionsChange([...collections, newCollection.trim()]);
      setNewCollection('');
    }
  };

  const handleRemoveCollection = (collectionToRemove: string) => {
    if (onCollectionsChange) {
      onCollectionsChange(collections.filter((c) => c !== collectionToRemove));
    }
  };

  const descriptionError = getFieldError('description');
  const descriptionHelp = descriptionError || (descAttr ? rt(descAttr.helpText) : undefined);

  return (
    <div className="flex flex-col gap-5">
      {/* Description */}
      <div>
        <label htmlFor="product-description" className="mb-2 block text-xs font-medium text-muted-foreground">
          {descAttr ? rt(descAttr.fieldLabel) : 'Description'}
        </label>
        <Textarea
          id="product-description"
          rows={4}
          value={description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder={descAttr ? (rt(descAttr.placeholder) || undefined) : 'Describe your product...'}
          aria-invalid={!!descriptionError || undefined}
          className="text-sm"
        />
        {descriptionHelp && (
          <p className={cn('mt-1.5 text-xs', descriptionError ? 'text-destructive' : 'text-muted-foreground')}>
            {descriptionHelp}
          </p>
        )}
      </div>

      {/* Collections */}
      {onCollectionsChange && (
        <div>
          <label htmlFor="product-collections" className="mb-2 block text-xs font-medium text-muted-foreground">
            {collectionsAttr ? rt(collectionsAttr.fieldLabel) : 'Collections'}
          </label>
          {collections.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {collections.map((collection) => (
                <Badge key={collection} variant="outline" className="gap-1 font-normal">
                  {collection}
                  <button
                    type="button"
                    onClick={() => handleRemoveCollection(collection)}
                    aria-label={`${t('COMMON.DELETE', 'Delete')} ${collection}`}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <X className="size-3.5" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <div className="relative">
            <Input
              id="product-collections"
              value={newCollection}
              onChange={(e) => setNewCollection(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddCollection()}
              placeholder={collectionsAttr?.placeholder ? rt(collectionsAttr.placeholder) : t('PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.ADD_COLLECTION_PLACEHOLDER')}
              className="h-9 pe-9 text-sm"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={handleAddCollection}
              disabled={!newCollection.trim()}
              aria-label={t('COMMON.ADD', 'Add')}
              className="absolute end-1 top-1"
            >
              <Plus />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface FamilyAttributesContentProps {
  attributes: FamilyAttribute[];
  values: Record<string, any>;
  onAttributeChange: (key: string, value: any) => void;
  errors?: Record<string, string>;
}

const FamilyAttributesContent: React.FC<FamilyAttributesContentProps> = ({
  attributes,
  values,
  onAttributeChange,
  errors = {},
}) => {
  // Sort attributes by their order property
  const sortedAttributes = [...attributes].sort((a, b) => a.order - b.order);

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {sortedAttributes.map((attr) => (
        <AttributeFieldRenderer
          key={attr.fieldName}
          attribute={attr}
          value={values[attr.fieldName]}
          onChange={(value) => onAttributeChange(attr.fieldName, value)}
          error={errors[attr.fieldName]}
        />
      ))}
    </div>
  );
};

// =============================================================================
// Variants Card Content - Combined axes editors + variant table (V2 style)
// =============================================================================

interface VariantsCardContentProps {
  family: ProductFamily;
  axisValues: Record<string, any[]>;
  onAxisValuesChange: (axisName: string, values: any[]) => void;
  onRegenerateVariants: () => void;
  variants: any[];
  variantGroups: any[];
  enabledAxes: any[];
  visualizationType: string;
  onVariantChange: (uuid: string, field: string, value: unknown) => void;
  onDeleteVariant: (uuid: string) => void;
  onSetDefault: (uuid: string) => void;
  onSelectVariant: (uuid: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onVisualizationChange: (uuid: string, field: string, value: unknown) => void;
  onReorderVariants?: (activeId: string, overId: string) => void;
  onAssignAxisValueToVariants?: (axisName: string, value: any) => void;
  unitDisplay?: string;
  // Material images state (lifted for submission)
  uploadedPlankImages: { [plankGroupId: string]: File[] };
  onUploadedPlankImagesChange: (images: { [plankGroupId: string]: File[] }) => void;
  // File upload for model3d products
  setPendingFile: (upload: { file: File; type: 'thumbnail' | 'material' | 'plank' | 'model' | 'originalImage' | 'usdz'; variantUuid: string }) => void;
  /** A picked file not uploaded yet (files are only sent on save) */
  getPendingFile?: (variantUuid: string, type: 'thumbnail' | 'material' | 'plank' | 'model' | 'originalImage' | 'usdz') => File | undefined;
  /** Available patterns from the selected product type (overrides family config) */
  productTypePatterns?: string[];
}

const VariantsCardContent: React.FC<VariantsCardContentProps> = ({
  family,
  axisValues,
  onAxisValuesChange,
  onRegenerateVariants,
  variants,
  variantGroups,
  enabledAxes,
  visualizationType,
  onVariantChange,
  onDeleteVariant,
  onSetDefault,
  onSelectVariant,
  onSelectAll,
  onVisualizationChange,
  onReorderVariants,
  onAssignAxisValueToVariants,
  unitDisplay,
  uploadedPlankImages,
  onUploadedPlankImagesChange,
  setPendingFile,
  getPendingFile,
  productTypePatterns,
}) => {
  const { t } = useTranslation();
  const textureAPI = useTextureApi();

  // State for generation status (plank images state is now lifted to parent)
  const [generatingVariants, setGeneratingVariants] = useState<Set<string>>(new Set());
  const [generating3DModels, setGenerating3DModels] = useState<Set<string>>(new Set());
  const [loadingPlankGroups, setLoadingPlankGroups] = useState<Set<string>>(new Set());

  // Helper to get size from variant axes
  const getSizeFromVariant = useCallback((variant: any): { width: number; height: number } => {
    const sizeAxis = variant.axes?.size;
    if (sizeAxis && typeof sizeAxis === 'object' && 'width' in sizeAxis) {
      // The generator works in cm, whatever unit the size was entered in.
      return { width: plankCm(sizeAxis.width, sizeAxis.unit), height: plankCm(sizeAxis.height, sizeAxis.unit) };
    }
    // Default size if not specified
    return { width: 20, height: 20 };
  }, []);

  // Helper to get pattern from variant axes
  const getPatternFromVariant = useCallback((variant: any): string => {
    const patternAxis = variant.axes?.pattern;
    if (typeof patternAxis === 'string') {
      return patternAxis;
    }
    // Default pattern
    return 'stackbond';
  }, []);

  // Helper to get grout settings from variant
  const getGroutFromVariant = useCallback((variant: any): { color: string; width: number } => {
    const grout = variant.visualization?.surface?.grout;
    return {
      color: grout?.color || '#00000000',
      width: grout?.width || 0,
    };
  }, []);

  // Build requirements instance for pattern (same logic as V2)
  const getRequirementInstance = useCallback((pattern: string, size: { width: number; height: number }, files: File[], grout: { color: string; width: number }, options?: { randomize?: boolean; mirror?: boolean }) => {
    if (!pattern || typeof pattern !== 'string') {
      logger.error(`Invalid pattern provided: ${pattern}`);
      return null;
    }

    const validFiles = files.filter(file => file instanceof File);
    if (validFiles.length === 0) {
      logger.error(`No valid files provided for pattern ${pattern}`);
      return null;
    }

    // Normalize pattern name: remove spaces, underscores, and hyphens
    // Handles: "stack_bond", "stack-bond", "Stack Bond", "stackbond" -> "stackbond"
    let normalizedPattern = pattern.toLowerCase().replace(/[\s_-]+/g, '');
    if (normalizedPattern === 'hexa') {
      normalizedPattern = 'hexagonal';
    }

    const baseConfig = {
      plankWidth: size.width,
      plankHeight: size.height,
      groutLineColor: grout.color,
      groutLineWidth: grout.width,
    };

    switch (normalizedPattern) {
      case 'bricks':
        return new BricksRequirements()
          .setFiles(validFiles)
          .setPlankWidth(baseConfig.plankWidth)
          .setPlankHeight(baseConfig.plankHeight)
          .setGroutLineColor(baseConfig.groutLineColor)
          .setGroutLineWidth(baseConfig.groutLineWidth)
          .setOffset('random');

      case 'chevron':
        return new ChevronRequirements()
          .setFiles(validFiles)
          .setPlankWidth(baseConfig.plankWidth)
          .setPlankHeight(baseConfig.plankHeight)
          .setGroutLineColor(baseConfig.groutLineColor)
          .setGroutLineWidth(baseConfig.groutLineWidth);

      case 'stackbond':
        return new StackBondRequirements()
          .setFiles(validFiles)
          .setPlankWidth(baseConfig.plankWidth)
          .setPlankHeight(baseConfig.plankHeight)
          .setGroutLineColor(baseConfig.groutLineColor)
          .setGroutLineWidth(baseConfig.groutLineWidth)
          .setRandomize(options?.randomize ?? true)
          .setMirror(options?.mirror ?? false);

      case 'herringbone':
        return new HerringboneRequirements()
          .setFiles(validFiles)
          .setPlankWidth(baseConfig.plankWidth)
          .setPlankHeight(baseConfig.plankHeight)
          .setGroutLineColor(baseConfig.groutLineColor)
          .setGroutLineWidth(baseConfig.groutLineWidth);

      case 'hexagonal':
        return new HexagonalRequirements()
          .setFiles(validFiles)
          .setLengthL1(baseConfig.plankWidth)
          .setLengthL2(baseConfig.plankHeight)
          .setGroutLineColor(baseConfig.groutLineColor)
          .setGroutLineWidth(baseConfig.groutLineWidth);

      // Shaped tiles (one photo of a single tile), flagstones (photos of single
      // stones, the size a typical stone) and alternating rows (the size of the
      // square tile): sized like a plank.
      case 'fishscale':
      case 'arabesque':
      case 'alure':
      case 'arrow':
      case 'penny':
      case 'triangle':
      case 'wave':
      case 'longhex':
      case 'flagstone':
      case 'flagstonetight':
      case 'alternating':
        return new PlankRequirements()
          .setFiles(validFiles)
          .setPlankWidth(baseConfig.plankWidth)
          .setPlankHeight(baseConfig.plankHeight)
          .setGroutLineColor(baseConfig.groutLineColor)
          .setGroutLineWidth(baseConfig.groutLineWidth);

      case 'cabochon':
        // Same split as the worker applies on save: octagons first half, squares second.
        const { octagons, squares } = splitCabochonImages(validFiles);
        return new CabochonRequirements()
          .setSquareImages(squares)
          .setOctoImages(octagons)
          .setSquareHeight(cabochonSquareCm(baseConfig.plankWidth, baseConfig.plankHeight))
          .setOctoHeight(baseConfig.plankWidth)
          .setGroutLineColor(baseConfig.groutLineColor)
          .setGroutLineWidth(baseConfig.groutLineWidth);

      case 'opus':
        const opusFiles = validFiles.length >= 4 ? validFiles :
          [...validFiles, ...validFiles, ...validFiles, ...validFiles].slice(0, 4);
        return new OpusRequirements()
          .setImageA(opusFiles[0] || validFiles[0])
          .setImageB(opusFiles[1] || validFiles[0])
          .setImageC(opusFiles[2] || validFiles[0])
          .setImageD(opusFiles[3] || validFiles[0])
          // The size is the smallest square (module L) — see utils/opusImages
          .setPlankSize(baseConfig.plankWidth, baseConfig.plankHeight)
          .setGroutLineColor(baseConfig.groutLineColor)
          .setGroutLineWidth(baseConfig.groutLineWidth);

      default:
        logger.error(`Unsupported pattern: ${normalizedPattern}`);
        return null;
    }
  }, []);

  // Plank images handlers
  const handlePlankImagesChange = useCallback((plankGroupId: string, files: File[]) => {
    onUploadedPlankImagesChange({ ...uploadedPlankImages, [plankGroupId]: files });
  }, [uploadedPlankImages, onUploadedPlankImagesChange]);

  const handleLoadPlankImages = useCallback(async (plankGroupId: string) => {
    // Skip if already loaded or loading
    if (uploadedPlankImages[plankGroupId]?.length > 0 || loadingPlankGroups.has(plankGroupId)) {
      return;
    }

    // Find a variant with this plankGroupId that has materialPaths (multiple mode)
    // or materialPath (single mode)
    // Note: plankGroupId falls back to variant.uuid when not explicitly set, so we apply the same fallback here
    const variantWithPlanks = variants.find(v => {
      const surfacePlankGroupId = v.visualization?.surface?.plankGroupId || v.uuid;
      const hasMaterialPaths = (v.visualization?.surface?.materialPaths?.length ?? 0) > 0;
      return surfacePlankGroupId === plankGroupId && hasMaterialPaths;
    });

    // Also look for single mode variants with materialPath
    const variantWithSingleTexture = variants.find(v => {
      const surfacePlankGroupId = v.visualization?.surface?.plankGroupId || v.uuid;
      const hasMaterialPath = !!v.visualization?.surface?.materialPath;
      const hasNoMaterialPaths = (v.visualization?.surface?.materialPaths?.length ?? 0) === 0;
      return surfacePlankGroupId === plankGroupId && hasMaterialPath && hasNoMaterialPaths;
    });

    const materialPaths = variantWithPlanks?.visualization?.surface?.materialPaths || [];
    const singleMaterialPath = variantWithSingleTexture?.visualization?.surface?.materialPath;

    // If no materialPaths and no single materialPath, nothing to load
    if (materialPaths.length === 0 && !singleMaterialPath) {
      return;
    }

    setLoadingPlankGroups(prev => new Set(prev).add(plankGroupId));

    try {
      // Fetch images from URLs and convert to Files
      const files: File[] = [];

      // Handle multiple mode (materialPaths)
      for (let i = 0; i < materialPaths.length; i++) {
        const url = materialPaths[i];
        try {
          const response = await fetch(url);
          if (!response.ok) {
            console.warn(`Failed to fetch plank image: ${url}`);
            continue;
          }
          const blob = await response.blob();
          const fileName = `${plankGroupId}_${i}.png`;
          const file = new File([blob], fileName, { type: blob.type || 'image/png' });
          files.push(file);
        } catch (err) {
          console.error(`Error fetching plank image ${url}:`, err);
        }
      }

      // Handle single mode (materialPath) - only if materialPaths was empty
      if (materialPaths.length === 0 && singleMaterialPath) {
        try {
          const fileName = `${plankGroupId}_seamless.png`;

          // Check if it's a URL (http/https) or base64
          if (singleMaterialPath.startsWith('http://') || singleMaterialPath.startsWith('https://')) {
            // It's a URL - fetch it
            const response = await fetch(singleMaterialPath);
            if (response.ok) {
              const blob = await response.blob();
              const file = new File([blob], fileName, { type: blob.type || 'image/png' });
              files.push(file);
            } else {
              console.warn(`Failed to fetch single texture: ${singleMaterialPath}`);
            }
          } else {
            // It's base64 - convert directly to File
            // Handle both "data:image/..." format and raw base64 string
            const base64Data = singleMaterialPath.startsWith('data:')
              ? singleMaterialPath
              : `data:image/png;base64,${singleMaterialPath}`;

            const response = await fetch(base64Data);
            const blob = await response.blob();
            const file = new File([blob], fileName, { type: blob.type || 'image/png' });
            files.push(file);
          }
        } catch (err) {
          console.error(`Error loading single texture ${singleMaterialPath}:`, err);
        }
      }

      // Update the uploadedPlankImages state
      if (files.length > 0) {
        onUploadedPlankImagesChange({ ...uploadedPlankImages, [plankGroupId]: files });
      }
    } catch (err) {
      console.error(`Error loading plank images for ${plankGroupId}:`, err);
    } finally {
      setLoadingPlankGroups(prev => {
        const next = new Set(prev);
        next.delete(plankGroupId);
        return next;
      });
    }
  }, [variants, uploadedPlankImages, loadingPlankGroups, onUploadedPlankImagesChange]);

  // Generate seamless material texture via API
  const handleGenerateMaterial = useCallback(async (variant: any, plankGroupId: string, files: File[], options?: { randomize?: boolean; mirror?: boolean }) => {
    const variantId = variant.uuid;
    const pattern = getPatternFromVariant(variant);
    const size = getSizeFromVariant(variant);
    const grout = getGroutFromVariant(variant);

    // Normalize pattern for API: "stack_bond", "Stack Bond" -> "stackbond"
    // Backend endpoints: seamless-stackbond, seamless-bricks, seamless-herringbone, etc.
    const normalizedPattern = pattern.toLowerCase().replace(/[\s_-]+/g, '');

    logger.info(`Generating material for variant ${variantId}, pattern: ${pattern} (normalized: ${normalizedPattern}), size: ${size.width}x${size.height}`);
    logger.info(`PlankGroupId: ${plankGroupId}, files: ${files.length}`);

    // Mark as generating
    setGeneratingVariants(prev => new Set(prev).add(variantId));

    try {
      // Build requirements for this pattern
      const requirements = getRequirementInstance(pattern, size, files, grout, options);
      if (!requirements) {
        throw new Error(`Failed to create requirements for pattern ${pattern}`);
      }

      // Call API to generate seamless texture (use normalized pattern for API endpoint)
      const result = await textureAPI.generateTextureForPattern(requirements, normalizedPattern);

      if (result) {
        // Update variant with generated texture.
        // `thumbnailPath` is intentionally not pre-set to the full seamless —
        // the cropper's onCropComplete → autoSaveThumbnail will produce a
        // properly-cropped thumbnail once the cropper loads the new texture
        // (see SurfaceExpandedContent's materialPath-watch effect). Until
        // then, ProductPreviewV3 CSS-zooms the materialPath fallback so the
        // preview reflects the cropper's framing instead of the full image.
        const imageData = typeof result === 'string' ? result : result.image;
        const imageUrl = `data:image/jpeg;base64,${imageData}`;
        onVisualizationChange(variantId, 'surface.materialPath', imageUrl);

        // Update texture dimensions from job result if available
        if (typeof result !== 'string' && result.textureWidth && result.textureHeight) {
          onVisualizationChange(variantId, 'surface.seamlessParams.textureWidth', result.textureWidth);
          onVisualizationChange(variantId, 'surface.seamlessParams.textureHeight', result.textureHeight);
        }
        // Keep how it was laid (stack bond mirror / order), so a later
        // regeneration or its grout layers lay it the same way.
        if (options) {
          if (typeof options.mirror === 'boolean') onVisualizationChange(variantId, 'surface.seamlessParams.mirror', options.mirror);
          if (typeof options.randomize === 'boolean') onVisualizationChange(variantId, 'surface.seamlessParams.randomize', options.randomize);
        }

        logger.info(`Successfully generated material for variant ${variantId}`);
      }
    } catch (error) {
      logger.error(`Failed to generate material for variant ${variantId}:`, error);
    } finally {
      // Remove from generating set
      setGeneratingVariants(prev => {
        const next = new Set(prev);
        next.delete(variantId);
        return next;
      });
    }
  }, [getPatternFromVariant, getSizeFromVariant, getGroutFromVariant, getRequirementInstance, textureAPI, onVisualizationChange]);

  // Visualization change handlers that delegate to onVisualizationChange
  const handleVariantZoomChange = useCallback((uuid: string, zoom: number) => {
    onVisualizationChange(uuid, 'surface.seamlessParams.zoom', zoom);
  }, [onVisualizationChange]);

  const handleVariantCropChange = useCallback((uuid: string, crop: { x: number; y: number }) => {
    onVisualizationChange(uuid, 'surface.seamlessParams.crop', crop);
  }, [onVisualizationChange]);

  const handleVariantGroutSizeChange = useCallback((uuid: string, size: number) => {
    onVisualizationChange(uuid, 'surface.grout.width', size);
  }, [onVisualizationChange]);

  const handleVariantGroutColorChange = useCallback((uuid: string, color: string) => {
    onVisualizationChange(uuid, 'surface.grout.color', color);
  }, [onVisualizationChange]);

  const handleSeamlessWidthChange = useCallback((uuid: string, width: number) => {
    onVisualizationChange(uuid, 'surface.seamlessParams.textureWidth', width);
  }, [onVisualizationChange]);

  const handleSeamlessHeightChange = useCallback((uuid: string, height: number) => {
    onVisualizationChange(uuid, 'surface.seamlessParams.textureHeight', height);
  }, [onVisualizationChange]);

  const handleVariantThumbnailChange = useCallback((uuid: string, thumbnailBase64: string) => {
    onVisualizationChange(uuid, 'thumbnailPath', thumbnailBase64);
  }, [onVisualizationChange]);

  // 3D model handlers
  const handleVariantImageChange = useCallback((uuid: string, imageFile: File) => {
    // Convert to base64 for UI preview in thumbnailPath
    const reader = new FileReader();
    reader.onload = () => {
      onVisualizationChange(uuid, 'thumbnailPath', reader.result as string);
    };
    reader.readAsDataURL(imageFile);

    // Queue the original full-resolution file for upload
    setPendingFile({
      file: imageFile,
      type: 'originalImage',
      variantUuid: uuid,
    });
  }, [onVisualizationChange, setPendingFile]);

  const handleVariantModelChange = useCallback((uuid: string, modelFile: File) => {
    // Queue the 3D model file for upload
    setPendingFile({
      file: modelFile,
      type: 'model',
      variantUuid: uuid,
    });
    // Set placeholder modelPath so UI shows "3D Model Ready"
    const variant = variants.find((v: any) => v.uuid === uuid);
    onVisualizationChange(uuid, 'model3d', {
      ...variant?.visualization?.model3d,
      modelPath: modelFile.name,
    });
  }, [setPendingFile, variants, onVisualizationChange]);

  const getPendingModelFile = useCallback(
    (uuid: string) => getPendingFile?.(uuid, 'model'),
    [getPendingFile],
  );

  const handleVariantUsdzChange = useCallback((uuid: string, usdzFile: File) => {
    // Queue the USDZ file for upload (iOS AR Quick Look asset)
    setPendingFile({
      file: usdzFile,
      type: 'usdz',
      variantUuid: uuid,
    });
    // Set placeholder usdzPath so UI shows "iOS AR Ready"
    const variant = variants.find((v: any) => v.uuid === uuid);
    onVisualizationChange(uuid, 'model3d', {
      ...variant?.visualization?.model3d,
      usdzPath: usdzFile.name,
    });
  }, [setPendingFile, variants, onVisualizationChange]);

  const handleGenerate3DModel = useCallback(async (uuid: string) => {
    // TODO: Implement 3D model generation via API
    setGenerating3DModels(prev => new Set(prev).add(uuid));
    console.log('Generate 3D model for variant:', uuid);
    // Simulated generation - replace with actual API call
    setTimeout(() => {
      setGenerating3DModels(prev => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    }, 3000);
  }, []);

  // Commerce link handlers — must update the full commerce object (setVariantField uses top-level keys)
  const handleVariantLinkChange = useCallback((uuid: string, link: string) => {
    const variant = variants.find((v: any) => v.uuid === uuid);
    onVariantChange(uuid, 'commerce', { ...(variant?.commerce || {}), link });
  }, [onVariantChange, variants]);

  const handleVariantAddToCartLinkChange = useCallback((uuid: string, link: string) => {
    const variant = variants.find((v: any) => v.uuid === uuid);
    onVariantChange(uuid, 'commerce', { ...(variant?.commerce || {}), addToCartLink: link });
  }, [onVariantChange, variants]);

  // Handler to delete multiple variants by their UUIDs
  // Used by AxisEditor when user confirms deletion of axis values with affected variants
  const handleDeleteVariants = useCallback((uuids: string[]) => {
    uuids.forEach((uuid) => {
      onDeleteVariant(uuid);
    });
  }, [onDeleteVariant]);

  return (
    <div className="flex flex-col gap-5">
      {/* Variant Table */}
      {variants.length > 0 && (
        <div>
          <VariantMatrix
            variants={variants}
            variantGroups={variantGroups}
            enabledAxes={enabledAxes}
            visualizationType={visualizationType as any}
            onVariantChange={onVariantChange}
            onDeleteVariant={onDeleteVariant}
            onSetDefault={onSetDefault}
            onSelectVariant={onSelectVariant}
            onSelectAll={onSelectAll}
            onReorderVariants={onReorderVariants}
            // Surface product props
            uploadedPlankImages={uploadedPlankImages}
            onPlankImagesChange={handlePlankImagesChange}
            onGenerateMaterial={handleGenerateMaterial}
            generatingVariants={generatingVariants}
            onVariantZoomChange={handleVariantZoomChange}
            onVariantCropChange={handleVariantCropChange}
            onVariantGroutSizeChange={handleVariantGroutSizeChange}
            onVariantGroutColorChange={handleVariantGroutColorChange}
            onSeamlessWidthChange={handleSeamlessWidthChange}
            onSeamlessHeightChange={handleSeamlessHeightChange}
            onVariantThumbnailChange={handleVariantThumbnailChange}
            onLoadPlankImages={handleLoadPlankImages}
            loadingPlankGroups={loadingPlankGroups}
            // 3D model props
            onVariantImageChange={handleVariantImageChange}
            onVariantModelChange={handleVariantModelChange}
            getPendingModelFile={getPendingModelFile}
            onVariantUsdzChange={handleVariantUsdzChange}
            onGenerate3DModel={handleGenerate3DModel}
            generating3DModels={generating3DModels}
            // Commerce link props
            onVariantLinkChange={handleVariantLinkChange}
            onVariantAddToCartLinkChange={handleVariantAddToCartLinkChange}
          />
        </div>
      )}

      {/* Empty state when no variants */}
      {variants.length === 0 && (
        <div className="border border-dashed bg-muted py-8 text-center">
          <p className="text-sm text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V3.EMPTY_VARIANTS')}
          </p>
        </div>
      )}
    </div>
  );
};

export default EditProductV3Page;
